/**
 * Länderspezifische Fachbegriffe — EINE Stelle statt verstreuter
 * `if (country === 'DE')` in den Formularen.
 *
 * Die Oberfläche spricht Österreichisch als Grundzustand ("Pflegestufe",
 * "Staatsbürgerschaft", "E-Card"). Für Agenturen mit Land DE tauscht `t()`
 * die hier gelisteten Schlüssel gegen ihr deutsches Gegenstück, bevor die
 * Sprachdatei gelesen wird. Das wirkt überall, wo der Schlüssel benutzt wird
 * (Formular, Feldsuche, Übersicht, Vorlagen-Platzhalter, Matching), ohne dass
 * die einzelnen Stellen das Land kennen müssen.
 *
 * Maßgeblich ist das Land der AGENTUR (Super-Admin → Agenturen → Land), nicht
 * die Sprache der Oberfläche: eine deutsche Agentur sieht auch auf Polnisch
 * den Pflegegrad, eine österreichische auch auf Hochdeutsch die Pflegestufe.
 * Öffentliche Seiten (Matching-Link, Klienten-Anmeldung) setzen das Land aus
 * den Daten des Links (`setTermCountry`).
 *
 * Felder, die für DE ganz entfallen oder andere Auswahlwerte/Prüfungen haben,
 * stehen in `germanProfile.ts` — hier geht es nur um Beschriftungen.
 */

export type TermCountry = 'AT' | 'DE' | 'CH';

export const toTermCountry = (country: string | null | undefined): TermCountry =>
  country === 'DE' || country === 'CH' ? country : 'AT';

type TermOverride = string | { key: string; params: Record<string, string | number> };

const level = (key: string, n: number): TermOverride => ({ key, params: { level: n } });

const GERMAN_TERMS: Record<string, TermOverride> = {
  // ── Pflegegrad (§ 15 SGB XI, 1–5) statt Pflegestufe (AT, 0–7) ──
  'fieldLabels.pflegestufe': 'german.careGrade',
  'fields.client.care_level': 'german.careGrade',
  'patients.careLevel': 'german.careGrade',
  'patients.list.careLevel': 'german.careGrade',
  'patientProfile.careLevel': 'german.careGrade',
  'matchingPublic.ui.careLevel': 'german.careGrade',
  'fieldLabels.pflegestufeWaehlen': 'german.careGradeSelect',
  'fieldLabels.pflegestufeLevel': 'german.careGradeLevel',
  'clientOverview.careLevel': 'german.careGradeLevel',
  'matchingPublic.ui.careLevelN': 'german.careGradeLevel',
  'patients.levelPrefix': 'german.careGradeShort',
  'patients.filterByCareLevel': 'german.careGradeFilter',
  'patients.allCareLevels': 'german.careGradeAll',
  'dashboard.cards.careLevelDistribution': 'german.careGradeDistribution',
  'dashboard.cards.noCareLevel Data': 'german.careGradeNoData',
  'fieldLabels.oesterreichischePflegestufe': 'german.careGradeHelp',
  'fieldLabels.pflegestufeBescheidVom': 'german.careGradeNoticeDate',
  'fieldLabels.datumDesPflegegeldbescheides': 'german.careGradeNoticeDateHelp',
  ...Object.fromEntries([1, 2, 3, 4, 5].flatMap((n) => [
    [`fieldLabels.pflegestufe${n}`, level('german.careGradeLevel', n)],
    [`fieldLabels.pflegestufe${n}Cb`, level('german.careGradeCb', n)],
    [`fieldLabels.wahrWennPflegestufe${n}`, level('german.careGradeTrueIf', n)],
  ])),
  // Stufe 6 und 7 gibt es in Deutschland nicht — die Platzhalter bleiben, sind aber gekennzeichnet.
  ...Object.fromEntries([6, 7].flatMap((n) => [
    [`fieldLabels.pflegestufe${n}`, level('german.austriaOnlyLevel', n)],
    [`fieldLabels.pflegestufe${n}Cb`, level('german.austriaOnlyLevel', n)],
    [`fieldLabels.wahrWennPflegestufe${n}`, level('german.austriaOnlyLevel', n)],
  ])),

  // ── Antrag auf einen Pflegegrad statt Pflegegeld-Antrag ──
  'fieldLabels.pflegegeldBeantragt': 'german.careGradeApplication',
  'fields.client.pflegegeld_application_status': 'german.careGradeApplication',
  'patients.pflegegeldApplicationStatus': 'german.careGradeApplication',
  'fieldLabels.pflegegeldStatus': 'german.careGradeApplication',
  'fieldLabels.pflegegeldstatus': 'german.careGradeApplication',
  'fieldLabels.statusDesPflegegeldAntrags': 'german.careGradeApplicationHelp',
  'fieldLabels.pflegegeldBewilligt': 'german.cgGranted',
  'fieldLabels.pflegegeldBewilligtCb': 'german.cgGranted',
  'fieldLabels.wahrWennPflegegeldBewilligtWurde': 'german.cgGranted',
  'fieldLabels.pflegegeldBeantragtCb': 'german.cgPending',
  'fieldLabels.wahrWennPflegegeldBeantragtInBearbeitung': 'german.cgPending',
  'fieldLabels.pflegegeldNichtBeantragt': 'german.cgNotApplied',
  'fieldLabels.pflegegeldNichtBeantragtCb': 'german.cgNotApplied',
  'fieldLabels.wahrWennPflegegeldNochNichtBeantragt': 'german.cgNotApplied',
  'fieldLabels.pflegegeldAbgelehntCb': 'german.cgRejected',

  // ── Krankenkasse + Krankenversichertennummer statt Versicherungsträger/-nummer ──
  'fieldLabels.versicherungstraeger': 'german.healthInsurer',
  'fieldLabels.krankenversicherung': 'german.healthInsurer',
  'fieldLabels.krankenversicherungstraegerDesKlienten': 'german.healthInsurerOfClient',
  'fieldLabels.versicherungsnummer': 'german.kvnr',
  'fieldLabels.krankenversicherungsnummer': 'german.kvnr',

  // ── Staatsangehörigkeit statt Staatsbürgerschaft ──
  'fieldLabels.staatsbuergerschaft': 'fieldLabels.staatsangehoerigkeit',
  'fields.client.citizenship': 'fieldLabels.staatsangehoerigkeit',
  'caregivers.form.optional.citizenship': 'fieldLabels.staatsangehoerigkeit',
  'clientOverview.caregiverProfile.citizenship': 'fieldLabels.staatsangehoerigkeit',
  'documentOcr.id.citizenship': 'fieldLabels.staatsangehoerigkeit',
  'fieldLabels.staatsbuergerschaftWaehlen': 'german.nationalitySelect',
  'caregivers.form.optional.citizenshipPlaceholder': 'german.nationalitySelect',
  'fieldLabels.staatsbuergerschaftDesKlienten': 'german.nationalityOfClient',
  'fieldLabels.staatsbuergerschaftDesBetreuers': 'german.nationalityOfCaregiver',

  // ── Sozialversicherungsnummer der Betreuungskraft (keine 10-stellige SVNR) ──
  'caregivers.form.administrative.svnr': 'german.socialInsuranceNumber',
  'caregivers.form.optional.svnr': 'german.socialInsuranceNumber',
  'fieldLabels.sozialversicherungsnummerSvnr': 'german.socialInsuranceNumber',
  'fieldLabels.svnr': 'german.socialInsuranceNumber',
  'caregivers.form.administrative.svnrPlaceholder': 'german.socialInsurancePlaceholder',

  // ── Nachweise ──
  'fieldLabels.eCard': 'german.healthCard',
  'documents.ecard': 'german.healthCard',
  'caregivers.documents.ecard': 'german.healthCard',
  'fieldLabels.eCardVorderseite': 'german.healthCardFront',
  'documents.ecardFront': 'german.healthCardFront',
  'caregivers.documents.ecardFront': 'german.healthCardFront',
  'fieldLabels.eCardRueckseite': 'german.healthCardBack',
  'documents.ecardBack': 'german.healthCardBack',
  'caregivers.documents.ecardBack': 'german.healthCardBack',
  'documents.strafregisterauszug': 'german.policeCertificate',
  'caregivers.documents.strafregisterauszug': 'german.policeCertificate',
  'fieldLabels.strafregisterauszug': 'german.policeCertificate',
  'documents.strafregisterauszugTranslation': 'german.policeCertificateTranslation',
  'caregivers.documents.strafregisterauszugTranslation': 'german.policeCertificateTranslation',
  'fieldLabels.strafregisterauszugUebersetzung': 'german.policeCertificateTranslation',
  'documents.dgkpDiplom': 'german.nurseDiploma',
  'caregivers.documents.dgkpDiplom': 'german.nurseDiploma',
  'fieldLabels.dgkpDiplom': 'german.nurseDiploma',
  'documents.dgkpDiplomTranslation': 'german.nurseDiplomaTranslation',
  'caregivers.documents.dgkpDiplomTranslation': 'german.nurseDiplomaTranslation',
  'fieldLabels.dgkpDiplomUebersetzung': 'german.nurseDiplomaTranslation',
  'professional.dgkpInHomeCountry': 'german.nurseInHomeCountry',
  'fieldLabels.dgkpImHerkunftsland': 'german.nurseInHomeCountry',
  'matchingPublic.caregiverFields.dgkpInHomeCountry': 'german.nurseInHomeCountry',
  'documents.gewerbeschein': 'german.tradeRegistration',
  'caregivers.documents.gewerbeschein': 'german.tradeRegistration',
  'fieldLabels.gewerbeschein': 'german.tradeRegistration',

  // ── Platzhalter in Eingabefeldern ──
  'fieldLabels.debitorennummerFuerBmdBuchhaltung': 'german.debtorNumberPlaceholder',
  'fieldLabels.atxxXxxxXxxxXxxxXxxx': 'german.ibanPlaceholder',
  'fieldLabels.vertretungsartEingeben': 'german.representationPlaceholder',
};

/**
 * Schweiz (Solenia, 08.10.2026): Beträge in CHF statt "in Euro", und
 * "Betreuungsdienst" statt "Agentur" bei den Tagessätzen.
 */
const SWISS_TERMS: Record<string, TermOverride> = {
  'caregivers.form.optional.dailyRatePlaceholder': 'swiss.dailyRatePlaceholder',
  'caregivers.form.optional.dailyFeeToAgencyPlaceholder': 'swiss.dailyFeeToServicePlaceholder',
  'fieldLabels.tagessatzAnAgentur': 'swiss.rateService',
  'fieldLabels.aktuellerTagessatzAnAgentur': 'swiss.currentRateService',
  'fieldLabels.verpflegungskosten': 'swiss.mealCosts',
  'fieldLabels.monatlicherVerpflegungskostenbeitragInEuro': 'swiss.mealContribution',
  'fieldLabels.transportkostenDesKlientenInEuro': 'swiss.transportCosts',
  'fieldLabels.aktuellerTagessatzDesKlientenInEuro': 'swiss.clientDailyRate',
  'fieldLabels.tagessatzInEuro': 'swiss.dailyRateChf',
  'fieldLabels.stundensatzInEuro': 'swiss.hourlyRateChf',
  'fieldLabels.svsKostenDesBetreuersInEuro': 'swiss.svsCosts',
  'fieldLabels.monatlichesNettoeinkommenDesKlientenInEuro': 'swiss.netIncome',
};

const TERMS: Partial<Record<TermCountry, Record<string, TermOverride>>> = { DE: GERMAN_TERMS, CH: SWISS_TERMS };

/** Schlüssel, den `t()` für dieses Land tatsächlich nachschlägt. */
export const countryTermKey = (key: string, country: TermCountry): string => {
  const override = TERMS[country]?.[key];
  if (!override) return key;
  return typeof override === 'string' ? override : override.key;
};

/** Feste Parameter eines Ersatzbegriffs (z. B. die Nummer des Pflegegrads). */
export const countryTermParams = (key: string, country: TermCountry): Record<string, string | number> | undefined => {
  const override = TERMS[country]?.[key];
  return override && typeof override !== 'string' ? override.params : undefined;
};

/** Für den Sprachdatei-Test: alle Ersatzschlüssel müssen existieren. */
export const allCountryTermTargets = (): string[] =>
  Object.values(TERMS).flatMap((table) =>
    Object.values(table || {}).map((o) => (typeof o === 'string' ? o : o.key)),
  );
